import React, { useState, useMemo, useCallback, useEffect } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
  Button,
  Typography,
  Chip,
} from "@mui/material";
import usePaginatedFirestore from "../../../hooks/usePaginatedFirestore";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { app as firebaseApp } from "../../../config/firebase";
import { getActiveHSGYCutoffYear } from "../../../utils/studentArchive";

import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { studentsFilters } from "../filterConfigurations";
import {
  calculateScholarshipStatus,
  calculateScholarshipEligible,
  calculateSeniorPacket,
  getSeniorPacketStatusText,
} from "../../../utils/scholarshipUtils";
import { formatDate } from "../../../utils/formatDate";

const db = getFirestore(firebaseApp);

// Functions exactly the same as the active Students page, but scoped to
// students whose HSGY falls before the trailing 5-year active window (see
// studentArchive.js) — i.e. everything the active Students page hides.
const StudentArchive = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Students"]?.edit;

  // HSGY is stored as a 4-digit String across virtually all existing
  // TpStudents records (not a Number), so the query must compare against a
  // String — Firestore range filters don't match across types.
  const cutoffYear = getActiveHSGYCutoffYear();
  const cutoffYearStr = String(cutoffYear);
  const archiveWhereConstraints = useMemo(
    () => [{ field: "HSGY", operator: "<", value: cutoffYearStr }],
    [cutoffYearStr],
  );

  const {
    data: students,
    loading,
    error,
    totalCount,
    currentPage,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
    goToPage,
    addDocument,
    updateDocument,
    deleteDocument,
    refresh,
  } = usePaginatedFirestore(
    "TpStudents",
    100,
    "LastName",
    "asc",
    archiveWhereConstraints,
  );

  // Lazy, one-time fetch of all archived students for filtering (getDocs, not
  // onSnapshot — no real-time listener overhead). Not loaded until needed —
  // see shouldLoadAll.
  const [allStudents, setAllStudents] = useState([]);
  const [allStudentsLoading, setAllStudentsLoading] = useState(false);

  // One-time fetches for pathway lookup: DDID → ProgramDelivery → PathwayID → PathwaysPrograms
  const [deliveryDetailsMap, setDeliveryDetailsMap] = useState({});
  const [programDeliveryMap, setProgramDeliveryMap] = useState({});
  const [pathwayMap, setPathwayMap] = useState({});

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [ddSnap, pdSnap, pathSnap] = await Promise.all([
          getDocs(collection(db, "DeliveryDetails")),
          getDocs(collection(db, "ProgramDelivery")),
          getDocs(collection(db, "PathwaysPrograms")),
        ]);

        // DeliveryDetails doc id → ProgramDeliveryID. DeliveryDetails
        // records have never actually had a DeliveryDetailsID field
        // populated (verified against live data — it's undefined on every
        // document), so this map was always empty — key by the real
        // Firestore document id instead, same id a student's
        // DeliveryDetailID is now saved as.
        const ddMap = {};
        ddSnap.docs.forEach((d) => {
          const data = d.data();
          ddMap[String(d.id).trim()] = String(
            data.ProgramDeliveryID || "",
          ).trim();
        });

        // ProgramDeliveryID → PathwayID
        const pdMap = {};
        pdSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.ProgramDeliveryID) {
            pdMap[String(data.ProgramDeliveryID).trim()] = String(
              data.PathwayID || "",
            ).trim();
          }
        });

        // PathwayID → { PathwayName, PathwayAbbrev }
        const pMap = {};
        pathSnap.docs.forEach((d) => {
          const data = d.data();
          const pid = String(data.PathwayID || d.id || "").trim();
          if (pid)
            pMap[pid] = {
              name: data.PathwayName || "",
              abbrev: data.PathwayAbbrev || "",
            };
        });

        setDeliveryDetailsMap(ddMap);
        setProgramDeliveryMap(pdMap);
        setPathwayMap(pMap);
      } catch (e) {
        console.error("Error fetching pathway lookups:", e);
      }
    };
    fetchLookups();
  }, []);

  // Only fetch the full archived set once the user actually needs it: when
  // they expand the filter panel. (The "Export Data" button with no active
  // filters falls back to its own live full-collection fetch in EditUpload
  // when exportData is empty, so it still works even if this hasn't loaded.)
  const [shouldLoadAll, setShouldLoadAll] = useState(false);

  useEffect(() => {
    if (!shouldLoadAll) return undefined;

    let cancelled = false;
    setAllStudentsLoading(true);
    const fetchAll = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "TpStudents"),
            where("HSGY", "<", cutoffYearStr),
            orderBy("HSGY", "asc"),
            orderBy("LastName", "asc"),
          ),
        );
        if (!cancelled) {
          setAllStudents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (e) {
        console.error("Error fetching all archived students for filters:", e);
      } finally {
        if (!cancelled) setAllStudentsLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [shouldLoadAll, cutoffYearStr]);

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [datagridFilterModel, setDatagridFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(allStudents || [], studentsFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
  };

  const pageName = "Archived Students";

  // Resolve pathway info from a student's DeliveryDetailID
  const getPathwayInfo = useCallback(
    (ddid) => {
      if (!ddid) return { name: "", abbrev: "" };
      const pdid = deliveryDetailsMap[String(ddid).trim()] || "";
      const pathwayId = programDeliveryMap[pdid] || "";
      return pathwayMap[pathwayId] || { name: "", abbrev: "" };
    },
    [deliveryDetailsMap, programDeliveryMap, pathwayMap],
  );

  const columns = useMemo(
    () => [
      {
        field: "Student_Guid",
        headerName: "Student Guid",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Application_Dt",
        headerName: "Application Date",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => formatDate(params.value),
        sortComparator: (v1, v2) => {
          const getTimestamp = (value) => {
            if (!value) return 0;

            // Handle Firestore Timestamp
            if (typeof value === "object" && value.seconds) {
              return value.seconds * 1000;
            }

            // Handle string dates (MM-DD-YYYY format)
            if (
              typeof value === "string" &&
              value.match(/^\d{1,2}-\d{1,2}-\d{4}$/)
            ) {
              const [month, day, year] = value.split("-");
              const date = new Date(year, month - 1, day);
              return isNaN(date.getTime()) ? 0 : date.getTime();
            }

            return 0;
          };
          return getTimestamp(v1) - getTimestamp(v2);
        },
      },
      { field: "LastName", headerName: "Last Name", flex: 1, minWidth: 120 },
      { field: "MI", headerName: "MI", flex: 1, minWidth: 80 },
      { field: "FirstName", headerName: "First Name", flex: 1, minWidth: 120 },
      {
        field: "DOB",
        headerName: "Date of Birth",
        flex: 1,
        minWidth: 130,
        renderCell: (params) => formatDate(params.value),
        sortComparator: (v1, v2) => {
          const getTimestamp = (value) => {
            if (!value) return 0;

            // Handle Firestore Timestamp
            if (typeof value === "object" && value.seconds) {
              return value.seconds * 1000;
            }

            // Handle string dates (MM-DD-YYYY format)
            if (
              typeof value === "string" &&
              value.match(/^\d{1,2}-\d{1,2}-\d{4}$/)
            ) {
              const [month, day, year] = value.split("-");
              const date = new Date(year, month - 1, day);
              return isNaN(date.getTime()) ? 0 : date.getTime();
            }

            return 0;
          };
          return getTimestamp(v1) - getTimestamp(v2);
        },
      },
      { field: "SCC_Email", headerName: "SCC Email", flex: 1, minWidth: 150 },
      {
        field: "ProgramDeliveryID",
        headerName: "Program Delivery ID",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "DeliveryDetailID",
        headerName: "Delivery Detail ID",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "SCCID",
        headerName: "Tartan ID",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const trimmed = String(params.value ?? "").trim();
          return trimmed ? trimmed.padStart(7, "0") : "";
        },
      },
      {
        field: "tp_pathway",
        headerName: "TP Pathway",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => {
          const { abbrev } = getPathwayInfo(params.row.DeliveryDetailID);
          return abbrev || "";
        },
        sortComparator: (v1, v2, p1, p2) => {
          const a =
            getPathwayInfo(p1.api.getRow(p1.id)?.DeliveryDetailID).abbrev || "";
          const b =
            getPathwayInfo(p2.api.getRow(p2.id)?.DeliveryDetailID).abbrev || "";
          return a.localeCompare(b);
        },
      },
      {
        field: "senior_packet",
        headerName: "Senior Packet Status",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const statusText = getSeniorPacketStatusText(params.row);
          return (
            <Typography variant="body2" color="text.primary">
              {statusText || "Not Applicable"}
            </Typography>
          );
        },
        sortable: true,
        filterable: true,
        sortComparator: (v1, v2, cellParams1, cellParams2) => {
          const val1 = getSeniorPacketStatusText(
            cellParams1.api.getRow(cellParams1.id),
          );
          const val2 = getSeniorPacketStatusText(
            cellParams2.api.getRow(cellParams2.id),
          );
          return (val1 || "").localeCompare(val2 || "");
        },
      },
      {
        field: "scholarship_status",
        headerName: "Scholarship Status",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const statusText = calculateScholarshipStatus(params.row);
          return (
            <Typography variant="body2" color="text.primary">
              {statusText || "Not Applicable"}
            </Typography>
          );
        },
        sortable: true,
        filterable: true,
        sortComparator: (v1, v2, cellParams1, cellParams2) => {
          const val1 = calculateScholarshipStatus(
            cellParams1.api.getRow(cellParams1.id),
          );
          const val2 = calculateScholarshipStatus(
            cellParams2.api.getRow(cellParams2.id),
          );
          return (val1 || "").localeCompare(val2 || "");
        },
      },
    ],
    [getPathwayInfo],
  );

  // Prepare data for DataGrid
  // Use paginated students for DataGrid rows
  const pagedRows = useMemo(() => {
    if (!students || !Array.isArray(students)) return [];
    return students.map((student, index) => ({
      id: student.id || index, // Use student.id if available, otherwise use index
      ...student,
    }));
  }, [students]);

  // Determine which rows to show: filteredData (if filters active) or pagedRows (default)
  const displayRows = hasActiveFilters
    ? filteredData.map((student, index) => ({
        id: student.id || index,
        ...student,
      }))
    : pagedRows;

  // DataGrid pagination state
  const [pageSize, setPageSize] = useState(100);
  const [clientPage, setClientPage] = useState(0);

  // Build export data: all fields from Firestore + computed columns appended
  const buildExportData = useCallback((sourceData) => {
    return sourceData.map((student) => ({
      ...student,
      senior_packet: getSeniorPacketStatusText(student) || "Not Applicable",
      scholarship_status:
        calculateScholarshipStatus(student) || "Not Applicable",
    }));
  }, []);

  // Utility: Get all unique field names from all students (for export)
  const allStudentFields = useMemo(() => {
    const allRows = Array.isArray(students) ? students : [];
    const fieldSet = new Set();
    allRows.forEach((row) => {
      Object.keys(row).forEach((key) => fieldSet.add(key));
    });
    return Array.from(fieldSet);
  }, [students]);

  // Simple toolbar without search (search is external)
  const CustomToolbar = useCallback(
    () => (
      <SimpleToolbar
        exportOptions={{
          csvOptions: {
            fileName: "archived-students",
            delimiter: ",",
            // Export all fields, not just columns
            fields: allStudentFields,
          },
          printOptions: {
            hideFooter: true,
            hideToolbar: true,
          },
        }}
      />
    ),
    [allStudentFields],
  );

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedStudent(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedStudent(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = (
    success = true,
    message = "Record has been updated successfully!",
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
    if (success) {
      setEditDialogOpen(false);
      refresh(); // Refresh the current page
    }
  };

  const handleRecordAdded = (
    success = true,
    message = "Record has been added successfully!",
  ) => {
    setSnackbarMessage(
      success && currentPage > 0
        ? "Record has been added successfully! Navigating to first page to show the new student."
        : message,
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
    if (success) {
      // Navigate to first page after adding to ensure new student is visible
      // Since students are sorted by LastName, new students appear based on alphabetical order
      if (currentPage > 0) {
        goToPage(0);
      } else {
        refresh(); // Just refresh if already on first page
      }
    }
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  // Fields users can actually fill in on the blank "Export Template": the
  // list columns plus every field with its own editable control in
  // Students/inc/Add.js and Edit.js (this page reuses those same Add/Edit
  // components). Excludes fields that are computed/display-only and have no
  // editable input of their own — senior_packet, scholarship,
  // scholarship_eligible, final_gpa, Secondary_Delivery_ID (all
  // auto-calculated), tp_pathway and scholarship_status (DataGrid-only
  // derived columns, not stored on the document), and SSN/SSN_encrypted/
  // SSN_Dashes/Last_Four_SSN/Password/Updated_By/Race/SSID/Note/
  // Residency_Status/added_to_portal (present in the form's initial state
  // but never actually rendered as an input).
  const studentTemplateFields = Array.from(
    new Set([
      ...columns
        .map((col) => col.field)
        .filter(
          (field) =>
            !["senior_packet", "scholarship_status", "tp_pathway"].includes(
              field,
            ),
        ),
      "App_StudentID",
      "Gender",
      "SSN_Status",
      "SSN_Status_Code",
      "verified_SSID",
      "sc_ctag_permission",
      "HSGY",
      "HomeSchool",
      "HomeSchoolID",
      "CCP_participant",
      "Alternate_Email",
      "OutReach_Status",
      "Address",
      "City",
      "State",
      "Zip",
      "PhoneNumber",
      "Sem7GPA",
      "Sem8GPA",
      "CTE_requirement",
      "MM3_ALG_II",
      "MM3_PreCalc",
      "CurrentEnrollmentStatus",
      "academic_program_interest",
      "reach_contact_id",
    ]),
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching students:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={studentsFilters}
          initialFilters={filterModel}
          onFiltersChange={handleFiltersChange}
          collapsible={true}
          defaultCollapsed={true}
          onExpand={() => setShouldLoadAll(true)}
          title={`Archived Student Filters${allStudentsLoading ? " (loading…)" : ""}`}
          resultCount={filteredData?.length || 0}
          totalCount={allStudents?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {canEdit && (
              <Add
                onRecordAdded={handleRecordAdded}
                addDocument={addDocument}
              />
            )}
            {canEdit && (
              <EditUpload
                collectionName="TpStudents"
                onActionComplete={handleBulkActionComplete}
                exportColumnOrder={columns.map((col) => col.field)}
                exportColumnLabels={{ SCCID: "Tartan ID" }}
                exportData={
                  hasActiveFilters
                    ? buildExportData(filteredData)
                    : buildExportData(allStudents)
                }
                exportFileName={
                  hasActiveFilters
                    ? "students-archive-filtered"
                    : "students-archive-all"
                }
                templateFields={studentTemplateFields}
                primaryKeyField="Student_Guid"
                booleanFields={[
                  "CCP_participant",
                  "verified_SSID",
                  "early_applicant_first_term",
                  "sc_ctag_permission",
                ]}
              />
            )}
            {canEdit && (
              <ClearCollectionButton
                collectionName="TpStudents"
                whereConstraints={archiveWhereConstraints}
                onCleared={refresh}
              />
            )}
          </Box>

          {/* Pagination Info and Controls (removed custom controls, handled by DataGrid) */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              label={`Showing HSGY before ${cutoffYear}`}
              color="default"
              variant="outlined"
            />
            <Chip
              label={`Total: ${totalCount.toLocaleString()} records`}
              color="info"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              Page {currentPage + 1} • Showing {students.length} records
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={refresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={displayRows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            filterModel={datagridFilterModel}
            onFilterModelChange={setDatagridFilterModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pagination
            paginationMode={hasActiveFilters ? "client" : "server"}
            rowCount={hasActiveFilters ? displayRows.length : totalCount}
            page={hasActiveFilters ? clientPage : currentPage}
            onPageChange={
              hasActiveFilters ? setClientPage : (newPage) => goToPage(newPage)
            }
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            hideFooter={false}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
        {/* Pagination Controls (removed bottom pagination controls) */}
      </div>
      {selectedStudent && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          studentDetail={selectedStudent}
          onRecordUpdated={handleRecordUpdated}
          updateDocument={updateDocument}
          deleteDocument={deleteDocument}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StudentArchive;
